import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { normalizeSitePath } from "../../shared/utils/book-edition-key.utils";
import { useBookPath, useBookSerializeToUrl } from "../../state/BookContext";
import { usePath } from "../../state/FileContext";
import { useFilterState } from "../../state/FilterContext";
import { InternalStateContext } from "../../state/InternalContext";
import { useSiteContext } from "../../state/SiteContext";
import { useVerifiedLinkSequence } from "../../state/verified-link-sequence.context";
import { findFirstComponentOfType, TextExtractor } from "../internal/TextExtractor";
import { buildVerifiedLinkHref, isSameVerifiedLinkTopic } from "./verified-link-href.utils";

interface SetCurrentVerifiedLinkButtonProps {
    index: string;
    label?: string;
    mode?: "relative" | "absolute";
    children: React.ReactNode;
    component?: "button" | "span" | "div";
    className?: string;
}

/**
 * Walks the page-visible LinkVerifier ok-link sequence (theme chrome may sit
 * outside the verifier). Same authoring shape as SetCurrentSearchHitButton.
 */
export function SetCurrentVerifiedLinkButton({
    index,
    label,
    children,
    mode = "relative",
    component = "div",
    className,
}: SetCurrentVerifiedLinkButtonProps): React.ReactElement {
    const { hasVerifier, settled, okPaths } = useVerifiedLinkSequence();
    const currentFilePath = usePath();
    const currentPath = normalizeSitePath(currentFilePath);
    const currentIndex = currentPath ? okPaths.indexOf(currentPath) : -1;
    const navigate = useNavigate();
    const location = useLocation();
    const filterState = useFilterState();
    const { siteState } = useSiteContext();
    const bookPath = useBookPath();
    const bookSerializeToUrl = useBookSerializeToUrl();
    const internalContext = useContext(InternalStateContext);

    const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
    const indexChild = useMemo(() => findFirstComponentOfType(children, "arg-index"), [children]);
    const [indexValue, setIndexValue] = useState(index);

    const parsedIndex = useMemo(() => parseInt(indexValue, 10), [indexValue]);
    const usedIndex = useMemo(() => {
        if (Number.isNaN(parsedIndex)) {
            return Number.NaN;
        }
        if (mode === "absolute") {
            return parsedIndex;
        }
        return currentIndex + parsedIndex;
    }, [currentIndex, mode, parsedIndex]);

    const [disabled, setDisabled] = useState(true);
    useEffect(() => {
        const outOfRange = Number.isNaN(usedIndex)
            || usedIndex < 0
            || usedIndex > okPaths.length - 1;
        setDisabled(!hasVerifier || !settled || okPaths.length === 0 || outOfRange);
    }, [hasVerifier, okPaths.length, settled, usedIndex]);

    const clickHandler: React.MouseEventHandler<HTMLButtonElement | HTMLDivElement | HTMLSpanElement> = useCallback(() => {
        const targetPath = okPaths[usedIndex];
        if (!targetPath || isSameVerifiedLinkTopic(currentFilePath, targetPath)) {
            return;
        }
        const href = buildVerifiedLinkHref({
            targetPath,
            pathname: location.pathname,
            search: location.search,
            sitePath: siteState.sitePath || "",
            isCustomDomain: internalContext?.internalState?.isCustomDomain === true,
            filterState,
            site: siteState.site,
            bookPath,
            bookSerializeToUrl,
        });
        if (href) {
            navigate(href);
        }
    }, [
        bookPath,
        bookSerializeToUrl,
        currentFilePath,
        filterState,
        internalContext?.internalState?.isCustomDomain,
        location.pathname,
        location.search,
        navigate,
        okPaths,
        siteState.site,
        siteState.sitePath,
        usedIndex,
    ]);

    const button = useMemo(() => {
        const usedLabel = label || labelChild?.props.children;
        const activeStateClass = mode === "absolute"
            ? (usedIndex === currentIndex ? "_active" : "_inactive")
            : undefined;
        const usedClassName = [
            className,
            disabled ? "_disabled" : "_enabled",
            activeStateClass,
        ].filter(Boolean).join(" ");
        const usedClickHandler = disabled ? undefined : clickHandler;
        switch (component) {
            case "button":
                return <button className={usedClassName} disabled={disabled} onClick={usedClickHandler}>{usedLabel}</button>;
            case "span":
                return <span className={usedClassName} onClick={usedClickHandler}>{usedLabel}</span>;
            case "div":
                return <div className={usedClassName} onClick={usedClickHandler}>{usedLabel}</div>;
            default:
                return <></>;
        }
    }, [className, clickHandler, component, currentIndex, disabled, label, labelChild, mode, usedIndex]);

    return <>
        {indexChild && (
            <TextExtractor onExtractedText={setIndexValue}>
                {indexChild?.props.children}
            </TextExtractor>
        )}
        {button}
    </>;
}
